import React from "react";
import { Link } from "react-router-dom";

const BUSINESS = {
  name: "Shoreline RV Rental",
  address: "609 N Nokomis St, Alexandria, MN 56308",
  phone: "(320) 634-6167",
  email: "info@shorelinervrental.com",
  website: "https://www.shorelinervrental.com",
};

export const SmsTermsPage = () => (
  <div className="legal-page" data-testid="sms-terms-page">
    <section className="page-header">
      <h1>SMS Terms &amp; Conditions</h1>
      <p>Shoreline RV Rental Booking Notifications Program</p>
    </section>
    <section className="legal-content">
      <div className="container">
        <p className="legal-updated">Last Updated: July 14, 2026</p>

        <h2>1. Program Description</h2>
        <p>
          The <strong>Shoreline RV Rental Booking Notifications</strong> program sends automated,
          transactional text (SMS) messages related to your RV rental booking, including: booking
          confirmations, deposit reminders, rental payment reminders, return-date notices, booking
          date-change confirmations, and availability alerts if your requested dates become
          unavailable.
        </p>

        <h2>2. Opt-In / Consent</h2>
        <p>
          You opt in to this program by checking the SMS consent checkbox on our online booking form
          at {BUSINESS.website} and providing your mobile phone number. Consent to receive text
          messages is <strong>not</strong> a condition of renting from {BUSINESS.name}. If you do
          not opt in, you will still receive all notifications by email.
        </p>

        <h2>3. Message Frequency</h2>
        <p>
          Message frequency varies based on your booking activity. You can expect approximately 3
          to 10 messages per rental booking. Recurring deposit reminders are sent no more than once
          every two days until your deposit is received.
        </p>

        <h2>4. Message &amp; Data Rates</h2>
        <p>
          <strong>Message and data rates may apply.</strong> Standard messaging rates from your
          wireless carrier apply to all messages sent and received. {BUSINESS.name} does not charge
          for this service.
        </p>

        <h2>5. Opt-Out Instructions</h2>
        <p>
          You can cancel the SMS service at any time. Text <strong>STOP</strong> to any message you
          receive from us. After you send <strong>STOP</strong>, we will send you a final SMS
          confirming that you have been unsubscribed, and you will no longer receive SMS messages
          from us. To re-join, text <strong>START</strong> to the same number or contact us. You may
          also request removal by calling {BUSINESS.phone} or emailing {BUSINESS.email}.
        </p>

        <h2>6. Help / Support</h2>
        <p>
          If you are experiencing issues with the messaging program, reply <strong>HELP</strong> to
          any message for assistance, call us at <strong>{BUSINESS.phone}</strong>, or email{" "}
          <strong>{BUSINESS.email}</strong>.
        </p>

        <h2>7. Carrier Disclaimer</h2>
        <p>
          Wireless carriers are not liable for delayed or undelivered messages. Supported carriers
          include (but are not limited to) AT&amp;T, T-Mobile, Verizon, and other major US carriers.
        </p>

        <h2>8. Privacy</h2>
        <p>
          Your mobile number and messaging consent are used solely to deliver the notifications
          described above and are never sold or shared with third parties for marketing purposes.
          See our <Link to="/privacy-policy">Privacy Policy</Link> for full details.
        </p>

        <h2>9. Contact</h2>
        <p>
          {BUSINESS.name}
          <br />
          {BUSINESS.address}
          <br />
          {BUSINESS.phone} | {BUSINESS.email}
        </p>
      </div>
    </section>
  </div>
);

export const PrivacyPolicyPage = () => (
  <div className="legal-page" data-testid="privacy-policy-page">
    <section className="page-header">
      <h1>Privacy Policy</h1>
      <p>{BUSINESS.name}</p>
    </section>
    <section className="legal-content">
      <div className="container">
        <p className="legal-updated">Last Updated: July 14, 2026</p>

        <h2>1. Information We Collect</h2>
        <p>When you book a rental or create an account with {BUSINESS.name}, we collect:</p>
        <ul>
          <li>Contact information: name, mailing address, email address, and phone number</li>
          <li>Booking details: rental dates, trailer selection, and payment status</li>
          <li>Account credentials: a password you create (stored in encrypted/hashed form)</li>
          <li>Your digital signature and acceptance of our rental terms</li>
          <li>SMS consent status, if you opt in to text notifications</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use your information only to:</p>
        <ul>
          <li>Process and manage your rental booking and customer account</li>
          <li>Send booking-related notifications by email and, with your consent, by SMS
            (confirmations, deposit and payment reminders, return notices, and availability alerts)</li>
          <li>Maintain our rental availability calendar</li>
          <li>Respond to your questions and provide customer support</li>
        </ul>

        <h2>3. SMS / Text Messaging Data</h2>
        <p>
          <strong>
            No mobile information will be shared with third parties or affiliates for marketing or
            promotional purposes.
          </strong>{" "}
          All the above categories exclude text messaging originator opt-in data and consent; this
          information will not be shared with, or sold to, any third parties. Text messaging opt-in
          data and consent are used solely to deliver the booking notifications you requested. See
          our <Link to="/sms-terms">SMS Terms &amp; Conditions</Link> for program details, message
          frequency, and opt-out instructions.
        </p>

        <h2>4. Information Sharing</h2>
        <p>
          We do not sell, rent, or trade your personal information. We share data only with the
          service providers required to operate our booking system (payment processing via Square,
          email delivery, SMS delivery via our messaging provider, and calendar scheduling), and
          only to the extent needed to provide those services, or when required by law.
        </p>

        <h2>5. Data Security</h2>
        <p>
          Passwords are stored using industry-standard one-way hashing (bcrypt). Data is
          transmitted over encrypted (HTTPS) connections. Payment card details are handled entirely
          by Square; we never see or store your card number.
        </p>

        <h2>6. Data Retention &amp; Your Choices</h2>
        <p>
          We retain booking records for business and legal purposes. You may opt out of SMS at any
          time by replying <strong>STOP</strong> to any message. To request access to, correction
          of, or deletion of your personal information, contact us using the details below.
        </p>

        <h2>7. Contact Us</h2>
        <p>
          {BUSINESS.name}
          <br />
          {BUSINESS.address}
          <br />
          {BUSINESS.phone} | {BUSINESS.email}
        </p>
      </div>
    </section>
  </div>
);
